import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Grid, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Autocomplete,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import PageContainer from '../../components/layout/PageContainer';
import { StockInFormData } from '../../types/inventory';
import { Item } from '../../types/item';
import { Location } from '../../types/location';

// Mock data - replace with API calls
const mockItems: Item[] = [
  {
    id: '1',
    itemCode: 'ITEM-001',
    itemName: 'Paracetamol',
    description: 'Pain reliever',
    price: 10.99,
    status: 'active',
    itemType: 'inventory',
    itemGroupId: 'GRP-001',
    unitOfMeasureId: 'UOM-001',
    reorderLevel: 50,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    itemGroup: {
      id: 'GRP-001',
      name: 'Pain Relief',
      code: 'PAIN'
    },
    unitOfMeasure: {
      id: 'UOM-001',
      name: 'Tablet',
      code: 'TAB'
    }
  },
  {
    id: '2',
    itemCode: 'ITEM-002',
    itemName: 'Ibuprofen',
    description: 'Anti-inflammatory',
    price: 15.50,
    status: 'active',
    itemType: 'inventory',
    itemGroupId: 'GRP-001',
    unitOfMeasureId: 'UOM-001',
    reorderLevel: 40,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    itemGroup: {
      id: 'GRP-001',
      name: 'Pain Relief',
      code: 'PAIN'
    },
    unitOfMeasure: {
      id: 'UOM-001',
      name: 'Tablet',
      code: 'TAB'
    }
  },
];

const mockLocations: Location[] = [
  {
    id: '1',
    name: 'Main Pharmacy',
    code: 'PH-001',
    locationType: 'Pharmacy',
    status: true,
    locationSubType: 'Inpatient',
    facilityId: 'FAC-001',
    facilityName: 'Main Facility',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Emergency Ward',
    code: 'WARD-ER',
    locationType: 'Ward',
    status: true,
    locationSubType: 'Emergency',
    facilityId: 'FAC-001',
    facilityName: 'Main Facility',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '3',
    name: 'OPD',
    code: 'WARD-OPD',
    locationType: 'Department',
    status: true,
    locationSubType: 'OPD',
    facilityId: 'FAC-001',
    facilityName: 'Main Facility',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
];

// Define the form data type based on the schema
type FormData = {
  locationId: string;
  reference?: string;
  notes?: string;
  items: Array<{
    itemId: string;
    quantity: number;
    batchNumber?: string;
    expiryDate?: string;
  }>;
};

const schema = yup.object({
  locationId: yup.string().required('Location is required'),
  reference: yup.string().optional(),
  notes: yup.string().optional(),
  items: yup.array().of(
    yup.object({
      itemId: yup.string().required('Item is required'),
      quantity: yup.number()
        .typeError('Quantity must be a number')
        .required('Quantity is required')
        .min(1, 'Quantity must be at least 1'),
      batchNumber: yup.string().optional(),
      expiryDate: yup.string().optional(),
    })
  ).min(1, 'At least one item is required').required(),
}).required();

const StockInPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { control, handleSubmit, formState: { errors }, reset, watch } = useForm<FormData>({
    resolver: yupResolver(schema as any), // Type assertion to fix yup and react-hook-form type mismatch
    defaultValues: {
      locationId: '',
      reference: '',
      notes: '',
      items: [{ itemId: '', quantity: 1, batchNumber: '', expiryDate: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const selectedLocationId = watch('locationId');

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      // TODO: Replace with actual API call
      console.log('Submitting stock in:', data);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      enqueueSnackbar('Stock in recorded successfully', { variant: 'success' });
      reset();
    } catch (error) {
      console.error('Error recording stock in:', error);
      enqueueSnackbar('Failed to record stock in', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addItemRow = () => {
    append({ 
      itemId: '', 
      quantity: 1, 
      batchNumber: '', 
      expiryDate: '' 
    });
  };
  
  const findItemById = (id: string) => {
    return mockItems.find(item => item.id === id);
  };

  return (
    <PageContainer title="Stock In">
      <Paper sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="locationId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.locationId}>
                    <InputLabel>Location *</InputLabel>
                    <Select
                      {...field}
                      label="Location *"
                      error={!!errors.locationId}
                    >
                      {mockLocations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name} ({location.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Items</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addItemRow}
                  disabled={!selectedLocationId}
                >
                  Add Item
                </Button>
              </Box>
              
              {fields.map((item, index) => (
                <Box key={item.id} sx={{ mb: 3, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Controller
                        name={`items.${index}.itemId`}
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            options={mockItems}
                            getOptionLabel={(option) => 
                              typeof option === 'string' ? option : `${option.itemCode} - ${option.itemName}`
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Item *"
                                error={!!errors.items?.[index]?.itemId}
                                helperText={errors.items?.[index]?.itemId?.message}
                              />
                            )}
                            onChange={(event: React.SyntheticEvent, value: Item | null) => {
                              field.onChange(value?.id || '');
                            }}
                            value={findItemById(field.value) || null}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <Controller
                        name={`items.${index}.quantity`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Quantity *"
                            fullWidth
                            inputProps={{ min: 1 }}
                            error={!!errors.items?.[index]?.quantity}
                            helperText={errors.items?.[index]?.quantity?.message}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <Controller
                        name={`items.${index}.batchNumber`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Batch #"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <Controller
                        name={`items.${index}.expiryDate`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="date"
                            label="Expiry Date"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={1} display="flex" alignItems="center">
                      {fields.length > 1 && (
                        <IconButton 
                          color="error" 
                          onClick={() => remove(index)}
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ))}
              
              {fields.length === 0 && (
                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                  No items added. Select a location and click "Add Item" to get started.
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Controller
                name="reference"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Reference #"
                    fullWidth
                    margin="normal"
                  />
                )}
              />
              
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => reset()}
                disabled={isSubmitting}
              >
                Reset
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={isSubmitting || fields.length === 0}
              >
                {isSubmitting ? 'Processing...' : 'Record Stock In'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </PageContainer>
  );
};

export default StockInPage;
